import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatState, ContactDetails, MatchedTutor, YearLevel } from "../types";
import { getCoursesByCurriculumId, getCurriculumsByYear } from "../lib/curriculums";
import { tutorMatchWithApproval } from "../lib/api";
import { AnalyticsEvents, track } from "../lib/analytics";
import { SubjectSearch } from "./SubjectSearch";
import { RobotHead } from "./RobotHead";
const WAYFINDER_BASE = "https://kisacademics.com";

/* ── Shared bubble components ────────────────────────────────── */

function TypingIndicator() {
  return (
    <div className="kis-chat-bg-gray-50 kis-chat-rounded-2xl kis-chat-rounded-tl-sm kis-chat-px-3 kis-chat-py-2 kis-chat-self-start kis-chat-border kis-chat-border-gray-100 kis-chat-text-gray-400 kis-chat-flex kis-chat-gap-1.5 kis-chat-items-center">
      <span className="kis-chat-typing-dot" />
      <span className="kis-chat-typing-dot" />
      <span className="kis-chat-typing-dot" />
    </div>
  );
}

function BotBubble({
  children,
  typing,
  onTypingDone,
}: {
  children: React.ReactNode;
  typing?: boolean;
  onTypingDone?: () => void;
}) {
  const [showContent, setShowContent] = useState(!typing);

  useEffect(() => {
    if (!typing) { setShowContent(true); return; }
    const t = setTimeout(() => {
      setShowContent(true);
      onTypingDone?.();
    }, 800);
    return () => clearTimeout(t);
  }, [typing]);

  if (!showContent) return <TypingIndicator />;

  return (
    <div className="kis-chat-bg-gray-50 kis-chat-rounded-2xl kis-chat-rounded-tl-sm kis-chat-px-2.5 kis-chat-py-1.5 kis-chat-max-w-[88%] kis-chat-self-start kis-chat-border kis-chat-border-gray-100">
      <p className="kis-chat-text-[13px] kis-chat-text-gray-700 kis-chat-leading-snug">{children}</p>
    </div>
  );
}

function UserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="kis-chat-self-end kis-chat-bg-primary kis-chat-text-white kis-chat-rounded-2xl kis-chat-rounded-tr-sm kis-chat-px-3 kis-chat-py-1.5 kis-chat-max-w-[85%] kis-chat-shadow-sm">
      <p className="kis-chat-text-[13px] kis-chat-leading-snug">{children}</p>
    </div>
  );
}

function TutorCards({
  tutors,
  source,
  subjectCode,
  subjectName,
}: {
  tutors: MatchedTutor[];
  source: "current" | "history";
  subjectCode?: string;
  subjectName?: string;
}) {
  return (
    <div className="kis-chat-flex kis-chat-flex-col kis-chat-gap-2.5 kis-chat-w-full">
      {tutors.map((tutor, index) => (
        <a
          key={tutor.id}
          href={`${WAYFINDER_BASE}/tutors/profiles/${tutor.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            track(AnalyticsEvents.TutorCardClicked, {
              source,
              position: index,
              tutor_id: tutor.id,
              tutor_slug: tutor.slug,
              tutor_name: [tutor.firstName, tutor.lastName].filter(Boolean).join(" "),
              subject_code: subjectCode,
              subject_name: subjectName,
            })
          }
          className="kis-chat-group kis-chat-block kis-chat-rounded-xl kis-chat-bg-white kis-chat-border kis-chat-border-gray-200 kis-chat-shadow-sm hover:kis-chat-shadow-md hover:kis-chat-border-accent/50 hover:kis-chat--translate-y-0.5 kis-chat-transition-all kis-chat-duration-200 kis-chat-overflow-hidden kis-chat-no-underline"
        >
          <div className="kis-chat-flex kis-chat-items-center kis-chat-gap-2.5 kis-chat-p-2.5">
            <div className="kis-chat-relative kis-chat-w-12 kis-chat-h-12 kis-chat-shrink-0 kis-chat-rounded-lg kis-chat-overflow-hidden">
              {tutor.profilePictureUrl ? (
                <img src={tutor.profilePictureUrl} alt={`${tutor.firstName}`} className="kis-chat-w-full kis-chat-h-full kis-chat-object-cover kis-chat-object-top" />
              ) : (
                <div className="kis-chat-w-full kis-chat-h-full kis-chat-bg-primary/5 kis-chat-flex kis-chat-items-center kis-chat-justify-center">
                  <span className="kis-chat-text-primary/40 kis-chat-font-display kis-chat-font-bold kis-chat-text-lg">{tutor.firstName?.[0] ?? "T"}</span>
                </div>
              )}
            </div>
            <div className="kis-chat-flex-1 kis-chat-min-w-0">
              <h3 className="kis-chat-font-display kis-chat-font-bold kis-chat-text-[12px] kis-chat-text-gray-900 group-hover:kis-chat-text-primary kis-chat-transition-colors kis-chat-leading-tight kis-chat-truncate">
                {[tutor.firstName, tutor.lastName].filter(Boolean).join(" ")}
              </h3>
              {tutor.caption && (
                <p className="kis-chat-text-[10px] kis-chat-text-gray-500 kis-chat-leading-snug kis-chat-line-clamp-1 kis-chat-mt-0.5">{tutor.caption}</p>
              )}
              <div className="kis-chat-flex kis-chat-items-center kis-chat-gap-1.5 kis-chat-mt-1">
                <span className="kis-chat-text-[9px] kis-chat-font-semibold kis-chat-text-accent kis-chat-bg-accent/10 kis-chat-px-1.5 kis-chat-py-px kis-chat-rounded">Free Trial</span>
                <span className="kis-chat-text-[9px] kis-chat-font-semibold kis-chat-text-gray-500">${tutor.price.hourlyCustomerCharge}/hr</span>
              </div>
            </div>
            <span className="kis-chat-inline-flex kis-chat-items-center kis-chat-justify-center kis-chat-px-2.5 kis-chat-py-1.5 kis-chat-rounded-full kis-chat-bg-accent kis-chat-text-white kis-chat-text-[10px] kis-chat-font-semibold kis-chat-flex-shrink-0 group-hover:kis-chat-bg-accent-hover kis-chat-transition-colors kis-chat-whitespace-nowrap">
              Book free trial
            </span>
          </div>
        </a>
      ))}
    </div>
  );
}

function SmallPill({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="kis-chat-px-3 kis-chat-py-1 kis-chat-rounded-full kis-chat-text-[11px] kis-chat-font-semibold kis-chat-transition-all kis-chat-duration-200 kis-chat-cursor-pointer kis-chat-border kis-chat-border-gray-200 kis-chat-text-primary kis-chat-bg-white hover:kis-chat-bg-primary hover:kis-chat-text-white hover:kis-chat-shadow-md hover:kis-chat-border-primary active:kis-chat-scale-95"
    >
      {children}
    </button>
  );
}

function ChatInput({
  placeholder,
  type,
  onSubmit,
  optional,
}: {
  placeholder: string;
  type?: string;
  onSubmit: (value: string) => void;
  optional?: boolean;
}) {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <form
      className="kis-chat-self-end kis-chat-w-full kis-chat-flex kis-chat-items-center kis-chat-gap-0 kis-chat-bg-white kis-chat-border kis-chat-border-gray-200 kis-chat-rounded-xl kis-chat-overflow-hidden kis-chat-transition-all focus-within:kis-chat-border-primary/50 focus-within:kis-chat-shadow-[0_0_0_3px_rgba(67,97,238,0.08)]"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(value.trim()); setValue("");
      }}
    >
      <input
        ref={inputRef}
        type={type || "text"}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="kis-chat-flex-1 kis-chat-px-4 kis-chat-py-3 kis-chat-bg-transparent kis-chat-text-[13px] kis-chat-text-gray-700 kis-chat-placeholder-gray-400 kis-chat-outline-none kis-chat-border-none"
      />
      <button
        type="submit"
        disabled={!optional && !value.trim()}
        className="kis-chat-w-8 kis-chat-h-8 kis-chat-my-1 kis-chat-mr-1.5 kis-chat-rounded-lg kis-chat-bg-primary kis-chat-text-white kis-chat-border-none disabled:kis-chat-opacity-30 hover:kis-chat-bg-primary-hover kis-chat-transition-all kis-chat-cursor-pointer kis-chat-flex-shrink-0 kis-chat-flex kis-chat-items-center kis-chat-justify-center"
      >
        <svg className="kis-chat-w-3.5 kis-chat-h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m-6-6l6 6-6 6" />
        </svg>
      </button>
    </form>
  );
}

function DelayedTyping({ delay, onDone }: { delay: number; onDone: () => void }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setShow(true), delay);
    const t2 = setTimeout(onDone, delay + 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [delay, onDone]);

  if (!show) return null;
  return <TypingIndicator />;
}

/* ── Year level label map ────────────────────────────────────── */

const YEAR_OPTIONS: { key: YearLevel; label: string }[] = [
  { key: "11_12", label: "Year 11-12" },
  { key: "7_10", label: "Year 7-10" },
  { key: "1_6", label: "Year 1-6" },
];

function yearLabel(y: YearLevel) {
  return YEAR_OPTIONS.find((o) => o.key === y)?.label ?? y;
}

/* ── Contact detail steps ────────────────────────────────────── */

type DetailStep = "ask-name" | "ask-email" | "ask-message" | "ask-phone" | "complete";


/* ── Main ChatWindow ─────────────────────────────────────────── */

interface PastRound {
  year: YearLevel;
  yearLabel: string;
  curriculum?: string;
  curriculumLabel?: string;
  subjectName?: string;
  tutors: MatchedTutor[];
  noResults: boolean;
  confirmChoice?: "yes" | "change" | "update-message" | null;
  updatedMessage?: string;
}

export function ChatWindow({ onClose }: { onClose: () => void }) {
  const [state, setState] = useState<ChatState>({ step: "greeting" });
  const [tutors, setTutors] = useState<MatchedTutor[]>([]);
  const [loading, setLoading] = useState(false);
  const [greetingDone, setGreetingDone] = useState(false);
  const [yearQuestionReady, setYearQuestionReady] = useState(false);
  const [pastRounds, setPastRounds] = useState<PastRound[]>([]);

  // Conversational contact collection
  const [detailStep, setDetailStep] = useState<DetailStep>("ask-name");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const [emailMsgDone, setEmailMsgDone] = useState(false);
  const [showDetailPrompt, setShowDetailPrompt] = useState(false);
  const [confirmChoice, setConfirmChoice] = useState<"yes" | "change" | "update-message" | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current!.scrollTop = scrollRef.current!.scrollHeight;
      }, 50);
    }
  }, [state, tutors, loading, greetingDone, yearQuestionReady, detailStep, contactName, contactEmail, contactMessage, contactPhone, emailMsgDone, showDetailPrompt, pastRounds]);

  const curriculums = useMemo(() => {
    if (state.year !== "11_12") return [];
    return getCurriculumsByYear("11_12");
  }, [state.year]);

  const courses = useMemo(() => {
    if (!state.curriculum) return [];
    return getCoursesByCurriculumId(state.curriculum);
  }, [state.curriculum]);

  /* ── Handlers ───────────────────── */

  const handleYearSelect = useCallback((year: YearLevel) => {
    track(AnalyticsEvents.SelectedYear, { year, year_label: yearLabel(year) });
    if (year === "11_12") {
      setState((s) => ({ ...s, step: "curriculum", year }));
    } else {
      setState((s) => ({ ...s, step: "subject", year, curriculum: year === "1_6" ? "primary" : "secondary" }));
    }
  }, []);

  const handleCurriculumSelect = useCallback((currId: string) => {
    const label = getCurriculumsByYear("11_12").find((c) => c.value === currId)?.label;
    track(AnalyticsEvents.SelectedCurriculum, { curriculum: currId, curriculum_label: label });
    setState((s) => ({ ...s, step: "subject", curriculum: currId }));
  }, []);

  const handleSubjectSelect = useCallback(
    (course: { code: string; name: string }) => {
      track(AnalyticsEvents.SelectedSubject, {
        subject_code: course.code,
        subject_name: course.name,
        curriculum: state.curriculum,
        year: state.year,
        has_existing_contact: Boolean(state.contact),
      });
      setState((s) => {
        const subject = { code: course.code, name: course.name, curriculumId: state.curriculum! };
        if (s.contact) {
          return { ...s, step: "confirm-contact", subject };
        }
        return { ...s, step: "details", subject };
      });
      if (!state.contact) setDetailStep("ask-name");
    },
    [state.curriculum, state.contact, state.year],
  );

  const handleConfirmContact = useCallback((action: "yes" | "change" | "update-message") => {
    track(AnalyticsEvents.ConfirmedContact, { action });
    setConfirmChoice(action);
    if (action === "yes") {
      setState((s) => ({ ...s, step: "searching" }));
    } else if (action === "change") {
      setState((s) => ({ ...s, step: "details", contact: undefined }));
      setContactName("");
      setContactEmail("");
      setContactMessage("");
      setContactPhone("");
      setDetailStep("ask-name");
    } else {
      setState((s) => ({ ...s, step: "update-message" }));
    }
  }, []);

  const handleDetailSubmit = useCallback((value: string) => {
    if (detailStep === "ask-name") {
      if (!value) return;
      setContactName(value);
      setDetailStep("ask-email");
    } else if (detailStep === "ask-email") {
      if (!value) return;
      setContactEmail(value);
      setDetailStep("ask-message");
    } else if (detailStep === "ask-message") {
      setContactMessage(value || "");
      setDetailStep("ask-phone");
    } else if (detailStep === "ask-phone") {
      setContactPhone(value || "");
      setDetailStep("complete");
    }
  }, [detailStep]);

  // Fire once when user enters the details form for the first time
  useEffect(() => {
    if (state.step === "details" && detailStep === "ask-name" && !contactName) {
      track(AnalyticsEvents.EnterSearchDetails, {
        subject_code: state.subject?.code,
        subject_name: state.subject?.name,
      });
    }
  }, [state.step, detailStep, contactName, state.subject?.code, state.subject?.name]);

  // Trigger search when details are complete
  useEffect(() => {
    if (detailStep !== "complete" || state.step !== "details") return;
    if (!state.year || !state.curriculum || !state.subject) return;

    const contact: ContactDetails = {
      fullName: contactName,
      email: contactEmail,
      message: contactMessage,
      phone: contactPhone || undefined,
    };

    track(AnalyticsEvents.SubmittedDetails, {
      has_message: Boolean(contactMessage),
      has_phone: Boolean(contactPhone),
    });

    setState((s) => ({ ...s, step: "searching", contact }));
  }, [detailStep]);

  useEffect(() => {
    if (state.step !== "searching") return;
    if (!state.year || !state.curriculum || !state.subject || !state.contact) return;

    track(AnalyticsEvents.SearchStarted, {
      year: state.year,
      curriculum: state.curriculum,
      subject_code: state.subject.code,
      subject_name: state.subject.name,
      attempt_number: pastRounds.length + 1,
    });

    setLoading(true);
    tutorMatchWithApproval({
      year: state.year,
      curriculum: state.curriculum,
      subject: state.subject,
      contact: state.contact,
    })
      .then((res) => {
        setTutors(res.tutors);
        if (res.tutors.length > 0) {
          track(AnalyticsEvents.TutorsListed, {
            count: res.tutors.length,
            subject_code: state.subject!.code,
            subject_name: state.subject!.name,
            curriculum: state.curriculum,
            year: state.year,
            tutor_ids: res.tutors.map((t) => t.id),
          });
        } else {
          track(AnalyticsEvents.NoTutorsFound, {
            subject_code: state.subject!.code,
            subject_name: state.subject!.name,
            curriculum: state.curriculum,
            year: state.year,
          });
        }
        setState((s) => ({
          ...s,
          step: res.tutors.length > 0 ? "results" : "no-results-ask",
        }));
      })
      .catch((err) => {
        track(AnalyticsEvents.SearchFailed, {
          subject_code: state.subject?.code,
          subject_name: state.subject?.name,
          curriculum: state.curriculum,
          year: state.year,
          error: err instanceof Error ? err.message : String(err),
        });
        setTutors([]);
        setState((s) => ({ ...s, step: "no-results-ask" }));
      })
      .finally(() => setLoading(false));
  }, [state.step]);

  const handleStartOver = useCallback(() => {
    track(AnalyticsEvents.SearchAgain, {
      previous_subject_code: state.subject?.code,
      previous_subject_name: state.subject?.name,
      previous_had_results: tutors.length > 0,
      round_number: pastRounds.length + 1,
    });
    // Snapshot current round into history
    setState((s) => {
      if (s.year) {
        const allCurriculums = s.year === "11_12" ? getCurriculumsByYear("11_12") : [];
        setPastRounds((prev) => [
          ...prev,
          {
            year: s.year!,
            yearLabel: yearLabel(s.year!),
            curriculum: s.curriculum,
            curriculumLabel: allCurriculums.find((c) => c.value === s.curriculum)?.label,
            subjectName: s.subject?.name,
            tutors,
            noResults: s.step === "no-results-ask",
            confirmChoice,
            updatedMessage: s.contact?.message,
          },
        ]);
      }
      return { step: "year", contact: s.contact };
    });
    setTutors([]);
    setYearQuestionReady(true);
    setEmailMsgDone(false);
    setShowDetailPrompt(false);
    setConfirmChoice(null);
    setContactName("");
    setContactEmail("");
    setContactMessage("");
    setContactPhone("");
    setDetailStep("ask-name");
  }, [tutors, confirmChoice, state.subject, pastRounds.length]);

  /* ── Render ─────────────────────── */

  return (
    <div className="kis-chat-flex kis-chat-flex-col kis-chat-h-full kis-chat-min-h-0">
      {/* Header */}
      <div className="kis-chat-flex-shrink-0 kis-chat-bg-primary kis-chat-px-5 kis-chat-py-4">
        <div className="kis-chat-flex kis-chat-justify-between kis-chat-items-center">
          <div className="kis-chat-flex kis-chat-items-center kis-chat-gap-3">
            <RobotHead size={52} className="kis-chat-rounded-full" />
            <div>
              <h2 className="kis-chat-font-display kis-chat-text-[18px] kis-chat-font-bold kis-chat-text-white kis-chat-leading-none kis-chat-mb-0.5">Clara</h2>
              <a href="https://kisacademics.com/" target="_blank" rel="noopener noreferrer" className="kis-chat-text-[10px] kis-chat-text-white/50 hover:kis-chat-text-white/80 kis-chat-transition-colors kis-chat-no-underline kis-chat-leading-none">Powered by KIS Academics</a>
            </div>
          </div>
          <div
            onClick={() => {
              track(AnalyticsEvents.ClosedChat, { step: state.step });
              onClose();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                track(AnalyticsEvents.ClosedChat, { step: state.step });
                onClose();
              }
            }}
            className="kis-chat-p-1 kis-chat-text-white/70 hover:kis-chat-text-white kis-chat-transition-colors kis-chat-cursor-pointer"
            aria-label="Close"
          >
            <svg className="kis-chat-w-5 kis-chat-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>
      </div>

      {/* Chat body */}
      <div ref={scrollRef} className="kis-chat-flex-1 kis-chat-min-h-0 kis-chat-overflow-y-auto kis-chat-p-4 kis-chat-bg-white kis-chat-flex kis-chat-flex-col" style={{ scrollbarWidth: "none" }}>
        <div className="kis-chat-mt-auto" />
        <div className="kis-chat-flex kis-chat-flex-col kis-chat-gap-3">

          {/* Greeting */}
          <BotBubble typing onTypingDone={() => setGreetingDone(true)}>
            Hey! No worries, I'll help you find a matching tutor.
          </BotBubble>

          {/* Past rounds */}
          {pastRounds.map((round, i) => (
            <div key={i} className="kis-chat-flex kis-chat-flex-col kis-chat-gap-3">
              <BotBubble>Which year level are you looking for?</BotBubble>
              <UserBubble>I'm looking for tutoring for {round.yearLabel}</UserBubble>
              {round.curriculum && round.curriculumLabel && (
                <>
                  <BotBubble>Which curriculum do you study?</BotBubble>
                  <UserBubble>{round.curriculumLabel}</UserBubble>
                </>
              )}
              {round.subjectName && (
                <>
                  <BotBubble>What subject are you looking for?</BotBubble>
                  <UserBubble>{round.subjectName}</UserBubble>
                </>
              )}
              {/* Confirm contact choice if applicable */}
              {round.confirmChoice && (
                <>
                  <BotBubble>We have your details. Is this correct?</BotBubble>
                  <UserBubble>
                    {round.confirmChoice === "yes" ? "Yes, correct" : round.confirmChoice === "update-message" ? "Update message only" : "Change details"}
                  </UserBubble>
                </>
              )}
              {round.confirmChoice === "update-message" && (
                <>
                  <BotBubble>What would you like us to know this time?</BotBubble>
                  <UserBubble>{round.updatedMessage || "—"}</UserBubble>
                </>
              )}
              {round.noResults && (
                <>
                  <BotBubble>We couldn't find an exact match right now, but don't worry — our team will review your request and get back to you within 24 hours!</BotBubble>
                  <BotBubble>What would you like to do?</BotBubble>
                  <UserBubble>Try again</UserBubble>
                </>
              )}
              {round.tutors.length > 0 && (
                <>
                  <BotBubble>Here are some tutors we found for you!</BotBubble>
                  <TutorCards
                    tutors={round.tutors}
                    source="history"
                    subjectCode={state.subject?.code}
                    subjectName={round.subjectName}
                  />
                  <UserBubble>Search again</UserBubble>
                </>
              )}
            </div>
          ))}

          {/* Year level */}
          {greetingDone && (
            <>
              <BotBubble typing={pastRounds.length === 0} onTypingDone={() => setYearQuestionReady(true)}>
                Which year level are you looking for?
              </BotBubble>

              {yearQuestionReady && !state.year && (
                <div className="kis-chat-self-end kis-chat-bg-white kis-chat-border kis-chat-border-gray-200 kis-chat-rounded-2xl kis-chat-rounded-tr-sm kis-chat-px-4 kis-chat-py-3 kis-chat-max-w-[85%]">
                  <p className="kis-chat-text-[13px] kis-chat-text-gray-500 kis-chat-mb-2">I'm looking for tutoring for</p>
                  <div className="kis-chat-flex kis-chat-flex-wrap kis-chat-gap-1.5 kis-chat-justify-end">
                    {YEAR_OPTIONS.map((opt) => (
                      <SmallPill key={opt.key} onClick={() => handleYearSelect(opt.key)}>{opt.label}</SmallPill>
                    ))}
                  </div>
                </div>
              )}
              {state.year && <UserBubble>I'm looking for tutoring for {yearLabel(state.year)}</UserBubble>}
            </>
          )}

          {/* Curriculum (Year 11-12 only) */}
          {state.step === "curriculum" && state.year === "11_12" && (
            <>
              <BotBubble>Which curriculum do you study?</BotBubble>
              <div className="kis-chat-flex kis-chat-flex-wrap kis-chat-gap-1.5 kis-chat-self-end kis-chat-justify-end kis-chat-max-w-[85%]">
                {curriculums.map((c) => (
                  <SmallPill key={c.value} onClick={() => handleCurriculumSelect(c.value)}>{c.label}</SmallPill>
                ))}
              </div>
            </>
          )}
          {state.curriculum && state.year === "11_12" && state.step !== "curriculum" && (
            <>
              <BotBubble>Which curriculum do you study?</BotBubble>
              <UserBubble>{curriculums.find((c) => c.value === state.curriculum)?.label ?? state.curriculum}</UserBubble>
            </>
          )}

          {/* Subject */}
          {state.step === "subject" && (
            <>
              <BotBubble>What subject are you looking for?</BotBubble>
              <div className="kis-chat-w-full">
                <SubjectSearch courses={courses} onSelect={handleSubjectSelect} />
              </div>
            </>
          )}
          {state.subject && state.step !== "subject" && (
            <>
              <BotBubble>What subject are you looking for?</BotBubble>
              <UserBubble>{state.subject.name}</UserBubble>
            </>
          )}

          {/* Confirm existing contact */}
          {(state.step === "confirm-contact" || (confirmChoice && state.step !== "details")) && state.contact && !contactName && (
            <>
              <BotBubble>
                We have your details: {state.contact.fullName} ({state.contact.email}).{state.contact.message ? ` Message: "${state.contact.message}"` : ""} Is this correct?
              </BotBubble>
              {!confirmChoice ? (
                <div className="kis-chat-flex kis-chat-flex-wrap kis-chat-gap-1.5 kis-chat-self-end kis-chat-justify-end kis-chat-max-w-[85%]">
                  <SmallPill onClick={() => handleConfirmContact("yes")}>Yes, correct</SmallPill>
                  <SmallPill onClick={() => handleConfirmContact("update-message")}>Update message only</SmallPill>
                  <SmallPill onClick={() => handleConfirmContact("change")}>Change details</SmallPill>
                </div>
              ) : (
                <UserBubble>
                  {confirmChoice === "yes" ? "Yes, correct" : confirmChoice === "update-message" ? "Update message only" : "Change details"}
                </UserBubble>
              )}
            </>
          )}

          {/* Update message only */}
          {(state.step === "update-message" || (confirmChoice === "update-message" && state.step === "searching")) && (
            <>
              <BotBubble>What would you like us to know this time?</BotBubble>
              {state.step === "update-message" ? (
                <ChatInput
                  placeholder="Goals, schedule preferences..."
                  optional
                  onSubmit={(val) => {
                    const msg = val || state.contact?.message || "";
                    setState((s) => ({
                      ...s,
                      step: "searching",
                      contact: { ...s.contact!, message: msg },
                    }));
                  }}
                />
              ) : state.contact?.message ? (
                <UserBubble>{state.contact.message}</UserBubble>
              ) : null}
            </>
          )}

          {/* Contact details — conversational, one at a time (only when freshly collecting) */}
          {(state.step === "details" || (contactName && (state.step === "searching" || state.step === "results" || state.step === "no-results-ask" || state.step === "done"))) && (
            <>
              {/* Name */}
              <BotBubble>Great choice! What's your name?</BotBubble>
              {contactName ? (
                <UserBubble>{contactName}</UserBubble>
              ) : (
                detailStep === "ask-name" && <ChatInput placeholder="Your name" onSubmit={handleDetailSubmit} />
              )}

              {/* Email */}
              {contactName && (
                <>
                  <BotBubble>Nice to meet you, {contactName.split(" ")[0]}! What's your email so we can send the results?</BotBubble>
                  {contactEmail ? (
                    <UserBubble>{contactEmail}</UserBubble>
                  ) : (
                    detailStep === "ask-email" && <ChatInput placeholder="Your email" type="email" onSubmit={handleDetailSubmit} />
                  )}
                </>
              )}

              {/* Message (optional) */}
              {contactEmail && (
                <>
                  <BotBubble>Anything you'd like us to know? (optional)</BotBubble>
                  {detailStep === "ask-message" ? (
                    <ChatInput placeholder="Goals, schedule preferences..." onSubmit={handleDetailSubmit} optional />
                  ) : contactMessage ? (
                    <UserBubble>{contactMessage}</UserBubble>
                  ) : null}
                </>
              )}

              {/* Phone (optional) */}
              {detailStep !== "ask-name" && detailStep !== "ask-email" && detailStep !== "ask-message" && contactEmail && (
                <>
                  <BotBubble>And a phone number? (optional)</BotBubble>
                  {detailStep === "ask-phone" ? (
                    <ChatInput placeholder="Phone number" type="tel" onSubmit={handleDetailSubmit} optional />
                  ) : contactPhone ? (
                    <UserBubble>{contactPhone}</UserBubble>
                  ) : null}
                </>
              )}
            </>
          )}

          {/* Searching */}
          {loading && <TypingIndicator />}

          {/* Results */}
          {state.step === "results" && tutors.length > 0 && (
            <>
              <BotBubble>Here are some tutors we found for you!</BotBubble>
              <TutorCards
                tutors={tutors}
                source="current"
                subjectCode={state.subject?.code}
                subjectName={state.subject?.name}
              />

              {/* Emailed confirmation */}
              <BotBubble typing onTypingDone={() => setEmailMsgDone(true)}>
                We've emailed these results to {state.contact?.email}.
              </BotBubble>

              {/* Delayed: more details prompt */}
              {emailMsgDone && !showDetailPrompt && <DelayedTyping delay={5000} onDone={() => setShowDetailPrompt(true)} />}
              {showDetailPrompt && (
                <>
                  <BotBubble>Would you like to see a more detailed page with all your matched tutors?</BotBubble>
                  <div className="kis-chat-flex kis-chat-flex-wrap kis-chat-gap-1.5 kis-chat-self-end kis-chat-justify-end kis-chat-max-w-[85%]">
                    <SmallPill onClick={() => {
                      const slugs = tutors.map((t) => t.slug).filter(Boolean).join(",");
                      const url = `${WAYFINDER_BASE}/tutors/callback/success?slugs=${slugs}`;
                      track(AnalyticsEvents.ViewDetailedResultsClicked, {
                        tutor_count: tutors.length,
                        subject_code: state.subject?.code,
                        subject_name: state.subject?.name,
                      });
                      track(AnalyticsEvents.RedirectedToDetailedResults, { url });
                      window.open(url, "_blank");
                    }}>
                      Yes, show me
                    </SmallPill>
                    {pastRounds.length < 2 && (
                      <SmallPill onClick={handleStartOver}>Search again</SmallPill>
                    )}
                    <SmallPill onClick={() => {
                      const url = state.year === "11_12" && state.curriculum
                        ? `${WAYFINDER_BASE}/tutors/${state.curriculum}/${state.subject?.code ?? ""}`
                        : `${WAYFINDER_BASE}/tutors/${state.subject?.code ?? ""}`;
                      track(AnalyticsEvents.RedirectedToBrowseSubjectTutors, {
                        url,
                        subject_code: state.subject?.code,
                        subject_name: state.subject?.name,
                        curriculum: state.curriculum,
                        year: state.year,
                        source: "results",
                      });
                      window.open(url, "_blank");
                    }}>
                      Browse {state.subject?.name} tutors
                    </SmallPill>
                  </div>
                </>
              )}
            </>
          )}

          {/* No results */}
          {state.step === "no-results-ask" && (
            <>
              <BotBubble>
                We couldn't find an exact match right now, but don't worry — our team will review your request and get back to you within 24 hours!
              </BotBubble>
              <BotBubble>What would you like to do?</BotBubble>
              <div className="kis-chat-flex kis-chat-flex-wrap kis-chat-gap-1.5 kis-chat-self-end kis-chat-justify-end kis-chat-max-w-[85%]">
                {pastRounds.length < 2 && (
                  <SmallPill onClick={handleStartOver}>Try again</SmallPill>
                )}
                <SmallPill onClick={() => {
                  const url = `${WAYFINDER_BASE}/tutors`;
                  track(AnalyticsEvents.RedirectedToBrowseAllTutors, {
                    url,
                    source: "no-results",
                    subject_code: state.subject?.code,
                    subject_name: state.subject?.name,
                  });
                  window.open(url, "_blank");
                }}>Browse all tutors</SmallPill>
                <SmallPill onClick={() => {
                  const url = state.year === "11_12" && state.curriculum
                    ? `${WAYFINDER_BASE}/tutors/${state.curriculum}/${state.subject?.code ?? ""}`
                    : `${WAYFINDER_BASE}/tutors/${state.subject?.code ?? ""}`;
                  track(AnalyticsEvents.RedirectedToBrowseSubjectTutors, {
                    url,
                    subject_code: state.subject?.code,
                    subject_name: state.subject?.name,
                    curriculum: state.curriculum,
                    year: state.year,
                    source: "no-results",
                  });
                  window.open(url, "_blank");
                }}>
                  Browse {state.subject?.name} tutors
                </SmallPill>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
